import { fetchAutocompleteResults } from '@/app/api/search'
import {
	DEFAULT_CHECK_IN_DATE,
	DEFAULT_CHECK_OUT_DATE,
	DEFAULT_CURRENCY,
	FORM_TYPE,
	PROPERTY_TYPE,
	RATING_COLOR,
	RATING_THRESHOLD,
	REDIRECT_PARTNERS,
	ROOM_GUESTS_SEPARATOR,
	localeMap,
} from '@/app/constants'
import countrytoCurrency from 'country-to-currency'
import {
	addDays,
	addMonths,
	addWeeks,
	differenceInDays,
	differenceInMilliseconds,
	formatISO,
	parseISO,
} from 'date-fns'
import { enGB } from 'date-fns/locale'
import queryString from 'query-string'
import { remove } from 'remove-accents'
import {
	Allocation,
	DestPagesProps,
	FormType,
	InternationalizedStringValue,
	LocationType,
	PageDataObject,
	SearchItemLocationType,
	SearchPlaceType,
} from './types'
import { SearchItem } from './types/index'

export function formatNumber(value: number) {
	const nFormat = new Intl.NumberFormat('en-US')
	return nFormat.format(value)
}

export function isAirport(value: string) {
	return value.toLowerCase() === SearchItemLocationType.AIRPORT
}

export function isFreeReg(value: string) {
	return value.toLowerCase() === SearchItemLocationType.FREEREGION
}

export const isFromUS = (countryCode: string) => {
	return countryCode.toUpperCase() === 'US'
}

export function isLandmark(value: string) {
	return value ? value.toLowerCase() === SearchItemLocationType.LM : false
}
export function isHood(value: string) {
	return value ? value.toLowerCase() === SearchItemLocationType.HOOD : false
}

export function getLocaleByCode(code: string) {
	if (code !== 'en') {
		return localeMap[code]
	}
	return enGB
}

export const getRentalContractDuration = (startDate: Date, endDate: Date) => {
	const diffDays = differenceInDays(endDate, startDate)
	const dta = differenceInMilliseconds(startDate, new Date())
	const dtaDiff = Math.ceil(dta / (1000 * 60 * 60 * 24))
	return { diffDays, dtaDiff }
}

export const getAdultsNum = (rooms: Allocation[]) => {
	return rooms
		.map((item) => {
			return item.adults
		})
		.join(ROOM_GUESTS_SEPARATOR)
}

export const getChildrenNum = (rooms: Allocation[]) => {
	return rooms
		.map((item) => {
			return item.childAge.length
		})
		.join(ROOM_GUESTS_SEPARATOR)
}

type RedirectTabData = SearchItem & {
	dateFrom: Date | null
	dateTo: Date | null
	roomsAndGuests: Allocation[]
	formType?: FormType
	backTabPartner?: string | null
	frontTabPartner?: string | null
	buid?: string | null
}

export const getFormData = (data: RedirectTabData) => {
	const checkInDate = data.dateFrom as Date
	const checkOutDate = data.dateTo || new Date()
	const buid = data.buid
	const ptid = data.ptid || ''
	// const fbp = getFbClientId();
	const partnerParams = {
		...(data.backTabPartner && { b: data.backTabPartner }),
		...(data.frontTabPartner || data.formType === FORM_TYPE.FRONT
			? { f: data.frontTabPartner || REDIRECT_PARTNERS.BOOKING }
			: {}),
	}

	let destinationId = ''
	let destinationKey
	let city = ''
	const { diffDays, dtaDiff } = getRentalContractDuration(
		checkInDate,
		checkOutDate
	)

	const cityName = decodeURIComponent(data.cityonly)

	let destination = data.displayname
	let locationName = data.displayname

	switch (data.kayakType) {
		case 'ap':
			destinationId = data.ap
			destinationKey = 'airport'
			city = cityName
			break
		case 'city':
			destinationId = ptid && 'C:' + ptid
			destinationKey = 'city'
			city = cityName
			break
		case 'hotel':
			destinationId = ptid && 'H:' + ptid.substring(1)
			destinationKey = 'hotel'
			city = cityName
			break
		default:
			break
	}

	if (data.googlePlaceId) {
		destination = data.cityonly
		locationName = `${data.cityonly}, ${data.country || ''}`
	}

	// const formatLocationName = (locationStr: string): string => {
	// 	if (!locationStr) return ''

	// 	let parts = locationStr
	// 		.split(' ')
	// 		.filter((part) => part.trim() !== '')
	// 		.map((part) => part.replace(/-/g, ' '))

	// 	// Remove 'freereg' if it's the first element
	// 	if (parts.length > 0 && parts[0].toLowerCase() === 'freereg') {
	// 		parts = parts.slice(1)
	// 	}

	// 	if (parts.length === 0) return ''
	// 	if (parts.length === 1) return parts[0].toLowerCase()

	// 	return parts.join(', ').toLowerCase()
	// }

	// const formattedLocationName =
	// 	data.kayakType === SearchItemLocationType.HOTEL
	// 		? locationName
	// 		: formatLocationName(locationName || '')

	const formData = {
		tab: data.formType?.toLowerCase() || FORM_TYPE.BACK.toLowerCase(),
		vert: 'hotels',
		'location-name': locationName,
		'hotel-country': remove(data.country || ''),
		'state-code': data.rc ? data.rc : data.region,
		...(data.cc && { 'country-code': data.cc }),
		'check-in-date': formatISO(checkInDate),
		'check-out-date': formatISO(checkOutDate),
		...(destinationId && { 'destination-id': destinationId }),
		'destination-key': destinationKey,
		destination,
		city: cityName,
		rooms: data.roomsAndGuests.length,
		_adults: getAdultsNum(data.roomsAndGuests),
		_children: getChildrenNum(data.roomsAndGuests),
		los: diffDays,
		dta: dtaDiff,
		...partnerParams,
		...(buid && { buid }),
		// ...(fbp && { fbp }),
	}

	return formData
}

export const addHiddenInputToForm = (
	form: HTMLFormElement,
	name: string,
	value: string
) => {
	const input = document.createElement('input')
	input.type = 'hidden'
	input.name = name
	input.setAttribute('value', value)
	form.appendChild(input)
}

export const submitHiddenForm = (
	data: Record<string, any>,
	form: HTMLFormElement
) => {
	form.innerHTML = '' // Clear previous inputs
	for (const key in data) {
		if (key.substring(0, 1) === '_') {
			const newKey = key.substring(1, key.length)
			const dataArray = data[key].split('|')
			for (let i = 0; i < dataArray.length; i++) {
				addHiddenInputToForm(form, newKey, dataArray[i])
			}
		} else {
			addHiddenInputToForm(form, key, data[key])
		}
	}
	form.submit()
}

export const smoothScrollTo = (to: number, offset = 10) => {
	if (typeof window === 'undefined') return
	window.scrollTo({
		top: to + offset,
		behavior: 'smooth',
	})
}

const getValueByLanguage = (
	array: InternationalizedStringValue[] | undefined,
	languageKey: string
) => {
	const localizedValue = array?.find((entry) => entry._key === languageKey)
	return localizedValue ? localizedValue.value : ''
}

export const getLocalizedValues = (data: PageDataObject, key: string) => {
	return {
		...data,
		headingOne: getValueByLanguage(data.headingOne, key),
		headingTwo: getValueByLanguage(data.headingTwo, key),
		metaTitle: getValueByLanguage(data.metaTitle, key),
		metaDescription: getValueByLanguage(data.metaDescription, key),
	}
}

// 8.3 -> 8.3
// 8 -> 8.0
// 10 -> 10
export function getRatingValue(num: number) {
	if (Number.isInteger(num) && num < 10) {
		return num.toFixed(1)
	}

	return num
}
// This is for title translation on the Hotels page
const getTargetLocation = async (params: DestPagesProps) => {
	const lang = params?.params.lang
	let [country, region, city, hotel] = params?.params.dest || []

	let loctype = params?.searchParams.loctype || null
	let searchQuery = ''

	if (hotel) {
		searchQuery = decodeURIComponent(hotel)
	} else if (city) {
		searchQuery = `${city}, ${region} ${country}`
	} else {
		searchQuery = `${region} ${country}`
	}

	if (country) {
		loctype = SearchItemLocationType.COUNTRY
	}

	if (region) {
		loctype = SearchItemLocationType.REGION
	}

	if (city) {
		loctype = SearchItemLocationType.CITY
	}

	if (city === SearchItemLocationType.AIRPORT) {
		loctype = SearchItemLocationType.AIRPORT
	}

	if (city === SearchItemLocationType.FREEREGION) {
		loctype = SearchItemLocationType.FREEREGION
	}

	if (hotel && city !== SearchItemLocationType.AIRPORT) {
		loctype = SearchItemLocationType.HOTEL
	}

	if (params?.searchParams.loctype) {
		loctype = params?.searchParams.loctype
	}

	const results = await fetchAutocompleteResults(
		searchQuery
			.split(' ')
			.filter((item) => item !== 'undefined')
			.join(' '),
		lang
	)
		.then((res) => {
			return loctype
				? res.find((item) => item.loctype === loctype) || res[0]
				: res[0]
		})
		.catch((e) => {
			console.log('Error getTargetLocation:', e)
			return null
		})

	return results
}

export const getDisplayData = async (params: DestPagesProps) => {
	const location = await getTargetLocation(params)

	const {
		country: displayCountry,
		cityonly,
		region: displayRegion,
		name: locationName,
		kayakType,
		displayname,
	} = location || {}

	const rawData = {
		cityonly,
		region: displayRegion,
		name: locationName,
		kayakType,
		displayname,
	}

	const filteredData = Object.fromEntries(
		Object.entries(rawData).filter(([key, value]) => value !== undefined)
	)

	const data = {
		country: displayCountry,
		city: filteredData.cityonly,
		displayName: displayname,
		...(filteredData.region === filteredData.cityonly
			? {}
			: { region: filteredData.region }),
		...(kayakType === SearchItemLocationType.HOTEL
			? { hotel: filteredData.name }
			: {}),
	}

	return data
}

export const getRatingColor = (rating: number): string => {
	switch (true) {
		case rating < RATING_THRESHOLD.LOW:
			return RATING_COLOR.LOW
		case rating < RATING_THRESHOLD.MEDUIM:
			return RATING_COLOR.MEDIUM
		default:
			return RATING_COLOR.HIGH
	}
}

//query : C:1231, R:4561, NYC
export const getLocationTypeByQuery = (query: string): LocationType | null => {
	if (!query) return null

	if (query.length === 3) {
		return LocationType.AIRPORT
	}

	const prefix = query.charAt(0).toLowerCase()
	const prefixToEndpointMap: { [key: string]: LocationType } = {
		c: LocationType.CITY,
		h: LocationType.HOTEL,
		r: LocationType.CITY,
	}

	const endpoint = prefixToEndpointMap[prefix]

	if (!endpoint) {
		return null
	}

	return endpoint
}

export const parseSearchParamsToLower = (
	searchParams?: Record<string, any>
) => {
	if (!searchParams) return null

	const params = new URLSearchParams(searchParams)
	const lowerCaseParams: { [key: string]: string } = {}
	params.forEach((value, key) => {
		if (
			typeof value === 'string' &&
			typeof key === 'string' &&
			value !== 'undefined'
		) {
			lowerCaseParams[key.toLowerCase()] = value
		}
	})

	return lowerCaseParams
}

export const getCurrencyByCountry = (countryCode: string): string => {
	if (!countryCode) return DEFAULT_CURRENCY

	const currency =
		countrytoCurrency[
			countryCode.toUpperCase() as keyof typeof countrytoCurrency
		]
	return currency || DEFAULT_CURRENCY
}

/**
 * Validates whether a given date string represents a date that is in the future.
 *
 * @param {string} [date] - The date string to be validated. It should be in ISO 8601 format.
 * @returns {string | null} - Returns the original date string if it is in the future, otherwise returns null.
 */
export function checkDate(date?: string | null): string | null {
	if (!date) return null

	const parsedDate = parseISO(date)
	return differenceInDays(parsedDate, new Date()) >= 0 ? date : null
}

/**
 * Validates if both check-in and check-out dates are present and valid (future dates)
 * @param checkIn - Check-in date string
 * @param checkOut - Check-out date string
 * @returns boolean indicating if both dates are valid
 */
export function hasValidDates(
	checkIn?: string | null,
	checkOut?: string | null
): boolean {
	return !!(checkIn && checkOut && checkDate(checkIn) && checkDate(checkOut))
}

/**
 * Checks if date parameters are missing or invalid for hotel searches
 * @param checkIn - Check-in date string
 * @param checkOut - Check-out date string
 * @returns boolean indicating if dates need fallback to defaults
 */
export function needsHotelDateFallback(
	checkIn?: string | null,
	checkOut?: string | null
): boolean {
	return !checkIn || !checkOut || !checkDate(checkIn) || !checkDate(checkOut)
}

/**
 * Validates if date parameters need fallback for compact booking display
 * @param checkInParam - Check-in date parameter
 * @param checkOutParam - Check-out date parameter
 * @returns boolean indicating if default dates should be used
 */
export function shouldUseCompactBookingDefaults(
	checkInParam?: string | null,
	checkOutParam?: string | null
): boolean {
	return (
		!checkInParam ||
		!checkOutParam ||
		!checkDate(checkInParam) ||
		!checkDate(checkOutParam)
	)
}

/**
 * Determines if default dates should be applied based on page type and date validity
 * @param checkInParam - Check-in date parameter from URL
 * @param checkOutParam - Check-out date parameter from URL
 * @param isMainPage - Whether this is the main page
 * @returns boolean indicating if default dates should be used
 */
export function shouldApplyDefaultDates(
	checkInParam?: string | null,
	checkOutParam?: string | null,
	isMainPage?: boolean
): boolean {
	return !!(
		!isMainPage &&
		(!checkInParam ||
			!checkOutParam ||
			(checkInParam && !checkDate(checkInParam)) ||
			(checkOutParam && !checkDate(checkOutParam)))
	)
}

/**
 * Gets the appropriate default dates based on location type
 * @param isHotel - Whether the selected location is a hotel
 * @returns object with checkIn and checkOut Date objects
 */
export function getDefaultDatesForLocationType(isHotel: boolean): {
	checkIn: Date
	checkOut: Date
} {
	if (isHotel) {
		const checkIn = addWeeks(new Date(), 1) // HOTEL_DEFAULT_CHECK_IN_WEEKS
		const checkOut = addDays(checkIn, 2) // HOTEL_DEFAULT_STAY_DAYS
		return { checkIn, checkOut }
	} else {
		return {
			checkIn: new Date(DEFAULT_CHECK_IN_DATE), // DEFAULT_CHECK_IN_DATE
			checkOut: new Date(DEFAULT_CHECK_OUT_DATE), // DEFAULT_CHECK_OUT_DATE
		}
	}
}

/**
 * Validates that dates are set and check-out date is not more than 12 months from today
 * @param dateFrom - Check-in date
 * @param dateTo - Check-out date
 * @returns boolean indicating if dates are valid and in range
 */
export function validateDateRange(
	dateFrom: Date | null,
	dateTo: Date | null
): boolean {
	if (!dateFrom || !dateTo) {
		return false
	}

	const maxAllowedDate = addMonths(new Date(), 12)
	if (dateTo > maxAllowedDate) {
		return false
	}

	return true
}

export const randomizeUserAgents = (userAgents: string[]): string => {
	const randomIndex = Math.floor(Math.random() * userAgents.length)
	return userAgents[randomIndex]
}

export const isBotUA = (userAgent: string) => {
	const lowerCaseAgent = userAgent.toLowerCase()
	const bots = [
		'bot',
		'spider',
		'crawler',
		'googlebot',
		'bingbot',
		'yandexbot',
		'bytespider',
		'google-read-aloud',
	]
	return bots.some((indicator) => lowerCaseAgent.includes(indicator))
}

export const removeMaxHeightFromUrl = (url: string) => {
	const fallbackImagePath = '/images/no-image.png'

	if (!url) {
		return fallbackImagePath
	}
	const urlObject = new URL(url)
	urlObject.searchParams.delete('maxheight')
	return urlObject.toString()
}

export const isImageVertical = (src: string) => {
	const image = new Image()
	image.src = src
	return image.naturalHeight > image.naturalWidth
}

export const validateEmail = (email: string) => {
	const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
	return re.test(String(email).toLowerCase())
}

export const isRtlLanguage = (lng: string) => {
	return ['ar', 'he'].includes(lng)
}
export const parseQueryString = (str: string) => {
	return queryString.parse(str)
}
export const serialize = (obj: Record<string, any>): string => {
	const str = []
	for (const p in obj) {
		if (obj.hasOwnProperty(p)) {
			str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]))
		}
	}
	return str.join('&')
}

export async function getPlaceDetails(placeId: string) {
	const { Place } = (await google.maps.importLibrary(
		'places'
	)) as google.maps.PlacesLibrary

	const place = new Place({
		id: placeId,
		requestedLanguage: 'en', // optional
	})

	// Call fetchFields, passing the desired data fields.
	await place.fetchFields({
		fields: [
			'displayName',
			'formattedAddress',
			'location',
			'id',
			'addressComponents',
			'utcOffsetMinutes',
			'photos',
			'primaryType',
			'primaryTypeDisplayName',
		],
	})

	const {
		displayName,
		formattedAddress,
		location,
		id,
		addressComponents,
		utcOffsetMinutes,
		photos,
		primaryType,
		primaryTypeDisplayName,
	} = place

	return {
		displayName,
		formattedAddress,
		location: {
			lat: location?.lat(),
			lng: location?.lng(),
		},
		id,
		utc: utcOffsetMinutes,
		addressComponents,
		displayImages: photos?.map((photo) => photo.getURI({ maxWidth: 200 })),
		displayType: {
			type: primaryType,
			displayName: primaryTypeDisplayName,
		},
	}
}
export const capitalize = (s: string) => {
	if (typeof s !== 'string') return ''
	return s.charAt(0).toUpperCase() + s.slice(1)
}

export const getGuestsCount = (rooms: Allocation[]) => {
	return rooms.reduce((total, room) => {
		return total + room.adults + room.childAge.length
	}, 0)
}

export const getValidDateFromParams = (
	searchParams: URLSearchParams,
	paramName: string,
	defaultDate: Date
): Date => {
	const dateParam = searchParams.get(paramName)
	if (dateParam && checkDate(dateParam)) {
		return parseISO(dateParam)
	}
	return defaultDate
}

export const getAppliedFiltersFromURL = (
	searchParams: URLSearchParams
): {
	stars: number[]
	rating: number[]
	facilities: number[]
	stayOptions: number[]
	hotelStyles: number[]
	priceRange: [number, number]
} => {
	const appliedFilters: {
		stars: number[]
		rating: number[]
		facilities: number[]
		stayOptions: number[]
		hotelStyles: number[]
		priceRange: [number, number]
	} = {
		stars: [],
		rating: [],
		facilities: [],
		stayOptions: [],
		hotelStyles: [],
		priceRange: [0, 0],
	}

	// Stars
	const starsParam = searchParams.get('s')
	if (starsParam) {
		appliedFilters.stars = starsParam.split('|').map(Number)
	}

	// Rating
	const ratingParam = searchParams.get('gr')
	if (ratingParam) {
		appliedFilters.rating = [Math.min(...ratingParam.split('|').map(Number))]
	}

	// Facilities
	const facilitiesParam = searchParams.get('f')
	if (facilitiesParam) {
		appliedFilters.facilities = facilitiesParam.split('|').map(Number)
	}

	// Stay Options
	const stayOptionsParam = searchParams.get('p')
	if (stayOptionsParam) {
		appliedFilters.stayOptions = stayOptionsParam.split('|').map(Number)
	}

	// Hotel Styles
	const hotelStylesParam = searchParams.get('t')
	if (hotelStylesParam) {
		appliedFilters.hotelStyles = hotelStylesParam.split('|').map(Number)
	}

	// Price Range
	const minPriceParam = searchParams.get('mn')
	const maxPriceParam = searchParams.get('mx')

	if (minPriceParam || maxPriceParam) {
		appliedFilters.priceRange = [
			minPriceParam ? parseInt(minPriceParam, 10) : 0,
			maxPriceParam ? parseInt(maxPriceParam, 10) : 0,
		]
	}

	return appliedFilters
}

export const isApartment = (value: number) => {
	return value === PROPERTY_TYPE.APARTMENT
}
export const isHotel = (value: number) => {
	return value === PROPERTY_TYPE.HOTEL
}

export const isCountry = (path: string | string[]) => {
	if (Array.isArray(path)) {
		return path.length === 1
	}
	return path.split('/').length === 1
}

export const scrollToSelectedMonth = (dateFrom: Date) => {
	const today = new Date()
	const monthContainers = document.querySelectorAll(
		'.react-datepicker__month-container'
	)

	const selectedDate = dateFrom || new Date()
	const monthsDiff =
		(selectedDate.getFullYear() - today.getFullYear()) * 12 +
		selectedDate.getMonth() -
		today.getMonth()

	if (monthContainers[monthsDiff] && monthsDiff >= 0) {
		const headerElement = monthContainers[monthsDiff].querySelector(
			'.react-datepicker__header'
		) as HTMLElement
		const container = document.querySelector(
			'.mobile-datepicker .react-datepicker'
		) as HTMLElement

		if (headerElement) {
			const offset = 35
			const scrollPosition = headerElement.offsetTop - offset
			headerElement.style.paddingTop = '20px'
			container.scrollTo({
				top: scrollPosition,
				behavior: 'instant',
			})
		} else {
			console.log('Header element not found in the selected month container')
		}
	} else {
		console.log('Selected month is not visible in the current view')
	}
}

export const calculateDisplayedMaxPrice = (
	minPrice: number,
	maxPrice: number
): number => {
	if (minPrice < 10) {
		return Math.min(Math.ceil(minPrice * 10), maxPrice)
	}

	const calculatedPrice = minPrice * 10
	const roundedPrice = Math.floor(calculatedPrice / 100) * 100

	if (roundedPrice > maxPrice) {
		let result
		if (maxPrice < 100) {
			result = Math.floor(maxPrice / 10) * 10
		} else {
			result = Math.floor(maxPrice / 100) * 100
		}

		// If the result is less than or equal to minPrice, just return maxPrice
		if (result <= minPrice) {
			return maxPrice
		}

		return result
	}

	return roundedPrice
}

export const determineSearchPlaceFromPath = (
	pathComponents: string[],
	hasHotelInPath: boolean
) => {
	const country = pathComponents[0] || ''
	const region = pathComponents[1] || ''
	const city = pathComponents[2] || ''
	const hotel = pathComponents[3] || ''
	const place = pathComponents[4] || ''

	let type: SearchPlaceType = SearchPlaceType.COUNTRY
	let name = country

	if (region) {
		type = SearchPlaceType.REGION
		name = `${region}, ${name}`
	}

	if (city) {
		type = SearchPlaceType.CITY
		name = `${city}, ${name}`
	}

	if (hotel) {
		type = SearchPlaceType.HOTEL
		name = hasHotelInPath ? hotel : `${hotel}, ${city}, ${country}`

		if (isLandmark(hotel) || isHood(hotel)) {
			type = SearchPlaceType.CITY // landmark search to city
			name = `${place}`
		}
	}

	if (isAirport(city)) {
		type = SearchPlaceType.AIRPORT
		name = `${hotel}, ${country}`
	}

	if (isFreeReg(city)) {
		type = SearchPlaceType.REGION
		name = `${country}`
	}

	return { type, name }
}

export const getRedirectPartnerInfo = (searchParams: URLSearchParams) => {
	const backTabPartner = searchParams.get('b') || null
	const frontTabPartner = searchParams.get('f') || null

	return {
		backTabPartner,
		frontTabPartner,
		hasBackTabRedirect: !!(backTabPartner && backTabPartner !== '0'),
		hasFrontPartner: !!(frontTabPartner && frontTabPartner !== '0'),
		isPartnerBooking: backTabPartner === 'b' || backTabPartner === 'bb',
	}
}

 export const initOpenReplay = async (uid: string, imid: string) => {
	const { tracker } = await import("@openreplay/tracker");
	const projectKey = process.env.NEXT_PUBLIC_OPENREPLAY_PROJECT_KEY
	const ingestPoint = process.env.NEXT_PUBLIC_OPENREPLAY_INGEST_POINT

	if (projectKey) {
		tracker.configure({
			projectKey,
			ingestPoint: ingestPoint,
			__DISABLE_SECURE_MODE: process.env.NODE_ENV !== 'production', // disable secure mode in development
		})
	}
	
 	if (typeof window !== 'undefined' && projectKey) {
      if (!tracker.isActive()) {
        try {
			await tracker.start({
				userID: uid,
				forceNew: false,
			} as any)
			
			tracker.setMetadata('uid', uid);
			tracker.setMetadata('imid', imid);
        } catch (error) {
          console.error("Failed to start OpenReplay tracker:", error)
        }
      }
    } else {
      console.warn("OpenReplay configuration missing: projectKey not found")
    }
}